import { useEffect, useState } from 'react';
import api from '../utils/api';

/**
 * Fetch a binary asset from an authenticated backend endpoint and return
 * a `blob:` URL suitable for `<img src>` / `<iframe src>` / `<a href>`.
 *
 * Why this exists: pre-2026-05 the Keystone thumbnail/report-pdf
 * streams were marked `isPublic` so plain `<img src="/keystone/...">`
 * worked without an Authorization header. That leaked attachments
 * across clinics. The endpoints now require JWT + clinic scope, but
 * the browser doesn't attach our bearer token to `<img>` requests —
 * so we fetch the bytes through the authed axios client and hand the
 * caller a blob URL instead.
 *
 * Lifecycle:
 *  - Returns `null` until the fetch resolves; switch on it to render a
 *    placeholder.
 *  - Revokes the previous blob URL when `path` changes or the
 *    component unmounts.
 *  - Pass `null` / `undefined` to no-op (useful for conditional inputs
 *    like `useAuthedBlobUrl(maybeUrl)` without breaking the Hook
 *    rules-of-call ordering).
 */
export function useAuthedBlobUrl(path: string | null | undefined): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!path) {
      setUrl(null);
      return;
    }

    let revoked = false;
    let createdUrl: string | null = null;

    api
      .get(path, { responseType: 'blob' })
      .then(res => {
        if (revoked) return;
        createdUrl = URL.createObjectURL(res.data);
        setUrl(createdUrl);
      })
      .catch(() => {
        if (revoked) return;
        setUrl(null);
      });

    return () => {
      revoked = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [path]);

  return url;
}

/**
 * One-shot fetch + browser-side download for an authed binary endpoint.
 * Use for download buttons where `<a href>` won't carry the bearer
 * token. Wraps the bytes in a temporary blob URL, programmatically
 * clicks an anchor, and revokes the URL on the next tick.
 */
export async function downloadAuthedBlob(path: string, filename: string): Promise<void> {
  const res = await api.get(path, { responseType: 'blob' });
  const blobUrl = URL.createObjectURL(res.data);
  try {
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Defer revoke so the browser has time to start the download.
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  }
}
